import { useState, useEffect } from 'react';
import type { PackagePlan } from '../types';

interface PackagePlanModalProps {
  isOpen: boolean;
  plan: PackagePlan | null;
  onClose: () => void;
  onSave: (planData: Partial<PackagePlan>) => void;
}

export default function PackagePlanModal({ isOpen, plan, onClose, onSave }: PackagePlanModalProps) {
  const [formData, setFormData] = useState<Partial<PackagePlan>>({
    name: '',
    validDays: 30,
    usageHours: 10,
    timeSlot: { type: 'unlimited' },
    price: 0,
    sort: 999,
  });

  const [discountType, setDiscountType] = useState<'gift' | 'reduction' | 'discount' | ''>('');
  const [discountValue, setDiscountValue] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (plan) {
      setFormData(plan);
      if (plan.discount) {
        setDiscountType(plan.discount.type);
        setDiscountValue(plan.discount.value);
      } else {
        setDiscountType('');
        setDiscountValue(0);
      }
    } else {
      setFormData({
        name: '',
        validDays: 30,
        usageHours: 10,
        timeSlot: { type: 'unlimited' },
        price: 0,
        sort: 999,
      });
      setDiscountType('');
      setDiscountValue(0);
    }
    setError('');
  }, [plan, isOpen]);

  const calculateDiscountedPrice = () => {
    const price = formData.price || 0;
    if (!discountType || !discountValue) return price;

    if (discountType === 'reduction') {
      return Math.max(0, price - discountValue);
    }
    if (discountType === 'discount') {
      return price * (discountValue / 10);
    }
    return price;
  };

  const generateName = () => {
    const days = formData.validDays || 0;
    const hours = formData.usageHours || 0;
    let name = `${days}天/${hours}小时`;
    if (formData.timeSlot?.type === 'limited' && formData.timeSlot.start && formData.timeSlot.end) {
      name += `/${formData.timeSlot.start}-${formData.timeSlot.end}`;
    }
    return name;
  };

  const handleSave = () => {
    if (!formData.validDays || formData.validDays <= 0) {
      setError('请输入有效的使用周期');
      return;
    }

    if (!formData.usageHours || formData.usageHours <= 0) {
      setError('请输入有效的使用时长');
      return;
    }

    if (!formData.price || formData.price <= 0) {
      setError('请输入有效的价格');
      return;
    }

    if (discountType === 'reduction' && discountValue >= formData.price) {
      setError('减免金额不能大于或等于原价');
      return;
    }

    if (formData.timeSlot?.type === 'limited') {
      if (!formData.timeSlot.start || !formData.timeSlot.end) {
        setError('请输入完整的时段范围');
        return;
      }
    }

    const discount =
      discountType && discountValue
        ? { type: discountType as 'gift' | 'reduction' | 'discount', value: discountValue }
        : undefined;

    const planData: Partial<PackagePlan> = {
      ...formData,
      name: generateName(),
      discount,
      discountedPrice: calculateDiscountedPrice(),
    };

    onSave(planData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {plan ? '编辑套餐' : '添加套餐'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              使用周期 (天) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.validDays}
              onChange={(e) => setFormData({ ...formData, validDays: Number(e.target.value) })}
              placeholder="购买后X天未使用的时长会过期"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              购买后{formData.validDays || 0}天未使用的时长会过期
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              使用时长 (小时) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.usageHours}
              onChange={(e) => setFormData({ ...formData, usageHours: Number(e.target.value) })}
              placeholder="请输入使用时长"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              使用时段 <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.timeSlot?.type === 'unlimited'}
                  onChange={() => setFormData({ ...formData, timeSlot: { type: 'unlimited' } })}
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">不限时段</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.timeSlot?.type === 'limited'}
                  onChange={() =>
                    setFormData({ ...formData, timeSlot: { type: 'limited', start: '', end: '' } })
                  }
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">输入时段</span>
              </label>
              {formData.timeSlot?.type === 'limited' && (
                <div className="ml-6 grid grid-cols-2 gap-4">
                  <input
                    type="time"
                    value={formData.timeSlot.start}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        timeSlot: { ...formData.timeSlot!, start: e.target.value },
                      })
                    }
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <input
                    type="time"
                    value={formData.timeSlot.end}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        timeSlot: { ...formData.timeSlot!, end: e.target.value },
                      })
                    }
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              价格 (元) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
              placeholder="请输入价格"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">优惠（可选）</label>
            <div className="space-y-3">
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">无优惠</option>
                <option value="gift">赠送</option>
                <option value="reduction">减免</option>
                <option value="discount">折扣</option>
              </select>

              {discountType && (
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    step={discountType === 'discount' ? '0.1' : '1'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(Number(e.target.value))}
                    placeholder={
                      discountType === 'gift'
                        ? '请输入赠送小时数'
                        : discountType === 'reduction'
                        ? '请输入减免金额'
                        : '请输入折扣（如8.5）'
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <span className="text-gray-600">
                    {discountType === 'gift' && '小时'}
                    {discountType === 'reduction' && '元'}
                    {discountType === 'discount' && '折'}
                  </span>
                </div>
              )}

              {discountType && discountValue > 0 && (
                <div className="bg-teal-50 border border-teal-200 px-4 py-2 rounded-lg text-sm text-teal-700">
                  优惠后价格：¥{calculateDiscountedPrice().toFixed(2)}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              排序 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.sort}
              onChange={(e) => setFormData({ ...formData, sort: Number(e.target.value) })}
              placeholder="数字越小越靠前"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-medium">套餐名称预览：</span>
              {generateName()}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
